import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { FileText, Upload, Download, Trash2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DOCUMENT_TYPES = [
  'Factura achizitie',
  'Factura achizitie vizata',
  'Certificat fiscal',
  'CIV',
  'Talon',
  'Documente externe',
  'Certificat de autenticitate',
  'CUI/CI'
];

export function DocumentsSection({ documents = [], uploadUrl, onRefresh, title = 'Documente' }) {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fisierul este prea mare (max 10MB)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadDocument = async () => {
    if (!selectedFile || !selectedDocType) {
      toast.error('Selectati fisierul si tipul documentului');
      return;
    }
    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', selectedDocType);
      await axios.post(API + uploadUrl, formData, {
        headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      toast.success('Document incarcat cu succes');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setSelectedDocType('');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la incarcare');
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Sigur doriti sa stergeti acest document?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      const deleteUrl = uploadUrl + '/' + docId;
      await axios.delete(API + deleteUrl, {
        headers: { Authorization: 'Bearer ' + token },
        withCredentials: true
      });
      toast.success('Document sters');
      onRefresh();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const downloadDocument = (doc) => {
    try {
      const byteCharacters = atob(doc.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Eroare la descarcare');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <FileText className="h-4 w-4" />
          {title} ({documents.length})
        </h4>
        <Button size="sm" onClick={() => { setSelectedFile(null); setSelectedDocType(''); setUploadDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="upload-doc-btn">
          <Upload className="h-4 w-4 mr-2" />
          Incarca document
        </Button>
      </div>

      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-white rounded border border-slate-200">
              <div className="flex items-center gap-3 flex-1">
                <FileText className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{doc.filename}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-medium text-blue-600">{doc.document_type}</span>
                    <span>{(doc.size / 1024).toFixed(1)} KB</span>
                    <span>{new Date(doc.uploaded_at).toLocaleDateString('ro-RO')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadDocument(doc)}><Download className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => deleteDocument(doc.doc_id)} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 text-center py-4">Niciun document incarcat</p>
      )}

      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Incarca document</DialogTitle>
            <DialogDescription>Selecteaza tipul si fisierul de incarcat</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tip document</Label>
              <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                <SelectTrigger><SelectValue placeholder="Selecteaza tipul documentului" /></SelectTrigger>
                <SelectContent>{DOCUMENT_TYPES.map((type) => (<SelectItem key={type} value={type}>{type}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fisier (max 10MB)</Label>
              <Input type="file" onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              {selectedFile && <p className="text-sm text-slate-500 mt-2">Selectat: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Anuleaza</Button>
              <Button onClick={uploadDocument} disabled={uploading || !selectedFile || !selectedDocType} className="bg-blue-600 hover:bg-blue-700 text-white">
                {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Se incarca...</> : <><Upload className="h-4 w-4 mr-2" />Incarca</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
